// Global Instructions Rule Applied!
//
// Configuration → Agent Gateway admin models.
//
// Shape mirrors public.view__agent_gateway_admin and
// public.view__agent_gateway_usage.
//
// Deliberately separate from agent_access_model.ts, which serves
// view__agent_products behind the Connected Agents approval tab: keeping the
// two contracts apart means a column added for this editor cannot break that
// screen's .strict() validation, and vice versa.
//
// The usage payload is display-only derived numbers with no edit path, so it is
// typed rather than Zod-validated.
import { z } from 'zod'
import { validateWithZodSchema } from '../../core/utils/utilities.ts'
import { AgentConfigValueKeySchema } from './internal_agents_config_model.ts'

export const AgentProductAdminRowSchema = z
  .object({
    agents__id: z.string(),
    agents__agent_key: z.string(),
    agents__name: z.string().nullish(),
    agents__description: z.string().nullish(),
    agents__client_name_raw: z.string().nullish(),
    // 'admin' (seeded) | 'dcr' (self-registered, quarantined until approved)
    agents__source: z.string().nullish(),
    agents__enabled: z.boolean().nullish(),
    // Enforced PER GRANT, not per product — agent_usage_consume keys its
    // counters on grant_key. 0 = uncapped for both.
    agents__requests_per_minute: z.number().nullish(),
    agents__daily_row_limit: z.number().nullish(),
    agents__environment: z.string().nullish(),
    agents__is_environment_override: z.boolean().nullish(),
    agents__is_effective: z.boolean().nullish(),
    agents__date_created: z.string().nullish(),
    // How many independent budgets a cap value actually creates.
    prog__active_grant_count: z.number().nullish()
  })
  .strict()

export type AgentProductAdminRow = z.infer<typeof AgentProductAdminRowSchema>

export const AgentToolAdminRowSchema = z
  .object({
    agent_tools__id: z.string(),
    agent_tools__tool_key: z.string(),
    agent_tools__name: z.string().nullish(),
    agent_tools__description: z.string().nullish(),
    agent_tools__kind: z.string().nullish(),
    agent_tools__enabled: z.boolean().nullish(),
    // The whitelisted per-call caps. allowed_modes / write_tables are NOT here
    // — live-write policy is deliberately outside this editor.
    agent_tools__value_caps: z.any(),
    agent_tools__environment: z.string().nullish(),
    agent_tools__is_environment_override: z.boolean().nullish(),
    agent_tools__is_effective: z.boolean().nullish(),
    // The whitelist filtered to this tool's kind, so the editor shows
    // max_per_day / max_amount only on write tools instead of six mostly-empty
    // fields on every row.
    prog__applicable_cap_keys: z.any()
  })
  .strict()

export type AgentToolAdminRow = z.infer<typeof AgentToolAdminRowSchema>

export function validateAgentProductAdminRow(
  data: unknown,
  options?: { isArray?: boolean }
): AgentProductAdminRow | AgentProductAdminRow[] {
  try {
    return validateWithZodSchema(
      AgentProductAdminRowSchema,
      data,
      'agent product admin row',
      options
    ) as AgentProductAdminRow | AgentProductAdminRow[]
  } catch (e) {
    throw new Error(`Agent product validation failed: ${e}`)
  }
}

export function validateAgentToolAdminRow(
  data: unknown,
  options?: { isArray?: boolean }
): AgentToolAdminRow | AgentToolAdminRow[] {
  try {
    return validateWithZodSchema(
      AgentToolAdminRowSchema,
      data,
      'agent tool admin row',
      options
    ) as AgentToolAdminRow | AgentToolAdminRow[]
  } catch (e) {
    throw new Error(`Agent tool validation failed: ${e}`)
  }
}

export { AgentConfigValueKeySchema }
export type { AgentConfigValueKey } from './internal_agents_config_model.ts'

export interface AgentGatewayUsageByAgent {
  agent_usage_counters__agent_key?: string
  agents__name?: string
  agents__requests_per_minute?: number
  agents__daily_row_limit?: number
  prog__requests_today?: number
  prog__rows_returned_today?: number
  prog__grant_count?: number
}

export interface AgentGatewayUsageByGrant {
  agent_usage_counters__grant_key?: string
  agent_usage_counters__agent_key?: string
  prog__user_name?: string
  prog__grant_status?: string
  prog__requests_today?: number
  prog__rows_returned_today?: number
  prog__peak_requests_per_minute?: number
  agent_usage_counters__last_bucket_start?: string
}

export interface AgentGatewayUsageData {
  since_utc?: string
  days?: number
  by_agent: AgentGatewayUsageByAgent[]
  by_grant: AgentGatewayUsageByGrant[]
  recent_minutes: Array<{
    agent_usage_counters__bucket_start?: string
    agent_usage_counters__agent_key?: string
    prog__requests?: number
  }>
}

export interface AgentGatewayConfigAdminData {
  resolvedEnvironment: string
  products: AgentProductAdminRow[]
  tools: AgentToolAdminRow[]
  capKeys: Array<z.infer<typeof AgentConfigValueKeySchema>>
}
